import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { registerDirtyCard } from '@/features/patient-record/lib/dirtyRegistry';
import { useDirtyScope } from '@/features/patient-record/lib/dirtyScope';
import { useAutosaveStatusStore } from '@/features/patient-record/lib/autosaveStatus';
import { useAutosaveEnabledStore } from '@/features/patient-record/lib/autosaveEnabled';
import { suppressSuccessToastDuring } from '@/features/patient-record/lib/toastSuppression';

/**
 * PTV2-1327 — the ONE editing primitive.
 *
 * Owns the whole draft lifecycle for any editable surface (record card, dialog, inline row) so the
 * cross-cutting behaviour is a property of the primitive, not something each component re-wires
 * (which is why the old per-surface approach lost data — see
 * `docs/features/PTV2-1274-editable-draft-primitive.md`). A component calls this once and renders;
 * everything below is handled uniformly:
 *
 *   • **Dirty tracking** — `isDirty` = draft differs from the last-synced server value (`baseline`).
 *   • **Registration** — auto-registers in the dirty registry while dirty (so the section-nav guard
 *     and pedigree node-switch flush see it) and unregisters when clean / unmounted. No `active` flag.
 *   • **isEditing** — derived from `:focus-within` on `containerProps.ref`; correct by default, so a
 *     background flush never commits the field under the cursor. Spread `containerProps` on the wrapper.
 *   • **Autosave** — debounced on **keystroke** (every `setDraft` resets it), publishing the countdown
 *     to {@link useAutosaveStatusStore} so a status surface can show "Autosave in Ns". A commit updates
 *     the draft in place and does NOTHING else — it never closes, refocuses, or tears down the field.
 *   • **Reseed-safety** — the draft re-syncs from a new server value ONLY when clean AND unfocused, so a
 *     background refetch / sibling save can never overwrite in-progress input ("leave for 2 min, come
 *     back exactly as left").
 *   • **canSave** — a create form with required fields still empty reports `false`; the registry's
 *     prompt disables Save instead of silently dropping the draft, and autosave stands down.
 */
export interface UseEditableDraftOptions<T> {
  /** The saved server view-model this surface edits. */
  readonly initial: T;
  /** Commit the draft (the surface's mutation). Fire-and-forget; may return a promise for `saving`. */
  readonly onSave: (draft: T) => unknown;
  /** Human-readable surface name, shown in the unsaved-changes prompt / logs. */
  readonly label: string;
  /** Can the draft be committed now? `false` when required fields are empty. Default: always. */
  readonly canSave?: (draft: T) => boolean;
  /** Dirty comparison. Default: structural (JSON) equality — matches the existing card snapshots. */
  readonly isEqual?: (a: T, b: T) => boolean;
  /** Translated field NAMES (no values) that differ from `baseline`, for the prompt. */
  readonly changedFields?: (draft: T, baseline: T) => readonly string[];
  /** ms of no-keystroke before autosave commits; resets on every `setDraft`. Default 2500. */
  readonly autosaveDebounceMs?: number;
  /** Opt out of autosave (e.g. a create form that must be explicitly submitted). Default true. */
  readonly autosave?: boolean;
}

export interface EditableDraft<T> {
  readonly draft: T;
  /** Update the draft; resets the autosave countdown. Accepts a value or an updater. */
  readonly setDraft: (next: T | ((prev: T) => T)) => void;
  readonly isDirty: boolean;
  readonly saving: boolean;
  readonly canSave: boolean;
  /**
   * PTV2-1327 — is autosave actually running for this surface right now? True when the surface is
   * autosave-capable AND the global autosave toggle is on. Surfaces use it to choose the Google-docs
   * "Saved" status (autosaveActive) over the explicit Save / Discard footer.
   */
  readonly autosaveActive: boolean;
  /** Commit now ("Save"). No-op when clean or not saveable. */
  readonly save: () => void;
  /** Throw the edits away and snap back to the current server value ("Discard"). */
  readonly discard: () => void;
  /**
   * Spread on the editable container so focus-within (isEditing) and reseed-safety work. `ref` is a
   * stable callback ref, so it attaches to any element type (div, form, section) without variance
   * friction.
   */
  readonly containerProps: { readonly ref: (node: HTMLElement | null) => void };
}

let nextInstanceId = 1;

const defaultEqual = <T>(a: T, b: T): boolean => JSON.stringify(a) === JSON.stringify(b);

export function useEditableDraft<T>(options: UseEditableDraftOptions<T>): EditableDraft<T> {
  const { initial, label } = options;
  const isEqual = options.isEqual ?? defaultEqual;
  const autosaveCapable = options.autosave ?? true;
  const debounceMs = options.autosaveDebounceMs ?? 2500;
  const scopeId = useDirtyScope();
  // PTV2-1327: autosave runs only when the surface is capable AND the global toggle is on. When off,
  // the surface shows explicit Save / Discard instead (Google-docs vs manual).
  const globalAutosaveEnabled = useAutosaveEnabledStore((s) => s.enabled);
  const autosaveActive = autosaveCapable && globalAutosaveEnabled;

  const instanceId = useMemo(() => `edraft-${String(nextInstanceId++)}`, []);
  const containerRef = useRef<HTMLElement | null>(null);
  const setContainer = useCallback((node: HTMLElement | null): void => {
    containerRef.current = node;
  }, []);

  const [draft, setDraftState] = useState<T>(initial);
  // The last server value we synced to; `isDirty` is measured against this, not the live `initial`,
  // so a background server change while the user is editing does not spuriously flip dirty/clean.
  const [baseline, setBaseline] = useState<T>(initial);
  const [saving, setSaving] = useState(false);

  const isDirty = !isEqual(draft, baseline);
  const canSave = options.canSave ? options.canSave(draft) : true;

  // Stable refs so timers / registry callbacks read live values without re-subscribing each render.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const baselineRef = useRef(baseline);
  baselineRef.current = baseline;
  const initialRef = useRef(initial);
  initialRef.current = initial;
  const isEqualRef = useRef(isEqual);
  isEqualRef.current = isEqual;
  const canSaveFnRef = useRef(options.canSave);
  canSaveFnRef.current = options.canSave;
  const autosaveActiveRef = useRef(autosaveActive);
  autosaveActiveRef.current = autosaveActive;
  const onSaveRef = useRef(options.onSave);
  onSaveRef.current = options.onSave;
  const changedFieldsRef = useRef(options.changedFields);
  changedFieldsRef.current = options.changedFields;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isFocused = useCallback((): boolean => {
    const el = containerRef.current;
    const active = typeof document === 'undefined' ? null : document.activeElement;
    return el !== null && active !== null && el.contains(active);
  }, []);

  const canSaveNow = useCallback((): boolean => {
    const fn = canSaveFnRef.current;
    return fn === undefined || fn(draftRef.current);
  }, []);

  // `suppressToast` on the AUTOMATIC paths (debounced autosave, teardown flush) so they don't spam
  // the success toast — the "Saved" status conveys it. An explicit Save (button) keeps its toast.
  const commitDraft = useCallback(
    (suppressToast: boolean): void => {
      if (isEqualRef.current(draftRef.current, baselineRef.current)) return;
      if (!canSaveNow()) return;
      const committed = draftRef.current;
      // Optimistic: mark clean immediately so the footer / countdown clear and a re-render doesn't
      // re-arm autosave. The mutation is optimistic too and reconciles the server query on settle.
      setBaseline(committed);
      setSaving(true);
      // Publish the in-flight commit so the GLOBAL status can show "Saving…" even though the card is
      // now optimistically clean (the dirty registry no longer reports it).
      useAutosaveStatusStore.getState().setSaving(instanceId, scopeId);
      const run = (): unknown => onSaveRef.current(committed);
      const promise = suppressToast
        ? suppressSuccessToastDuring(run)
        : Promise.resolve(run());
      void promise.finally(() => {
        useAutosaveStatusStore.getState().clearSaving(instanceId);
        if (mountedRef.current) setSaving(false);
      });
    },
    [canSaveNow, instanceId, scopeId],
  );
  const doSave = useCallback((): void => {
    commitDraft(false);
  }, [commitDraft]);

  const setDraft = useCallback((next: T | ((prev: T) => T)): void => {
    setDraftState((prev) => (typeof next === 'function' ? (next as (p: T) => T)(prev) : next));
  }, []);

  const discard = useCallback((): void => {
    // Revert to the current server truth (the latest `initial`), not the possibly-older baseline.
    setDraftState(initialRef.current);
    setBaseline(initialRef.current);
    setPrevInitial(initialRef.current);
  }, []);

  // Reseed-safety: when a new server value arrives, adopt it ONLY if the user isn't mid-edit — clean
  // and unfocused. Otherwise keep the draft (and leave `baseline` put, so dirtiness stays measured
  // against what the user started from). Done during render (the derived-state pattern) so `isDirty`
  // is correct on the same frame — no one-frame footer flicker.
  //
  // Compare by VALUE, not reference: many callers build `initial` inline (`toDraft(record)`), a new
  // object every render. A reference check (`initial !== prevInitial`) would then fire the setState
  // below on EVERY render → "Too many re-renders". Value equality only fires when the data actually
  // changed, so callers never have to memoize `initial`.
  const [prevInitial, setPrevInitial] = useState(initial);
  if (!isEqualRef.current(initial, prevInitial)) {
    setPrevInitial(initial);
    const dirtyNow = !isEqualRef.current(draftRef.current, baselineRef.current);
    if (!dirtyNow && !isFocused()) {
      setDraftState(initial);
      setBaseline(initial);
    }
  }

  // Autosave: (re)arm a debounced commit whenever the draft is dirty and saveable; every keystroke
  // resets it. Publishes the fire time so a status surface can render the live countdown.
  useEffect(() => {
    const store = useAutosaveStatusStore.getState();
    if (!autosaveActive || !isDirty || !canSave) {
      store.clearPending(instanceId);
      return undefined;
    }
    store.setPending(instanceId, { scopeId, savesAt: Date.now() + debounceMs });
    const timer = window.setTimeout(() => {
      useAutosaveStatusStore.getState().clearPending(instanceId);
      commitDraft(true); // autosave → suppress the success toast
    }, debounceMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [draft, isDirty, canSave, autosaveActive, debounceMs, scopeId, instanceId, commitDraft]);

  // Release the countdown + in-flight entries on unmount.
  useEffect(
    () => () => {
      useAutosaveStatusStore.getState().clearPending(instanceId);
      useAutosaveStatusStore.getState().clearSaving(instanceId);
    },
    [instanceId],
  );

  // Register in the shared dirty registry while dirty, so the section-nav guard and the pedigree
  // node-switch/deselect flush see this surface. `isEditing` is correct by construction here.
  useEffect(() => {
    if (!isDirty) return undefined;
    return registerDirtyCard({
      scopeId,
      label,
      // Teardown/nav flushes are automatic → suppress the toast (like autosave).
      save: () => {
        commitDraft(true);
      },
      discard: () => {
        discard();
      },
      changedFields: () => changedFieldsRef.current?.(draftRef.current, baselineRef.current) ?? [],
      isEditing: () => isFocused(),
      canSave: () => canSaveNow(),
      autosaveActive: () => autosaveActiveRef.current,
      // This surface owns its idle autosave (keystroke-reset debounce above), so the pedigree's
      // legacy 10s idle-flush must not also commit it — it would double-save an edit or, worse,
      // auto-create a create-mode form. Teardown flushes (node-switch / before-save) still commit it.
      autosaveOwned: true,
    });
  }, [isDirty, scopeId, label, commitDraft, discard, isFocused, canSaveNow]);

  return {
    draft,
    setDraft,
    isDirty,
    saving,
    canSave,
    autosaveActive,
    save: doSave,
    discard,
    containerProps: { ref: setContainer },
  };
}
